<template>
    <div>
        <Button
            @click="openModal()"
            color="light"
            :size="buttonSize"
            iconName="share-nodes"
            class="w-full"
        >
            Share
        </Button>

        <Modal
            :show="isModalOpen"
            @close="closeModal()"
        >
            <template #title>
                <div class="text-gray-800 text-start font-bold text-lg">
                    Link to Share
                </div>
            </template>
            <template #content>
                <div class="w-full">
                    <div
                        class="flex flex-col md:flex-row items-end md:items-center gap-1 mt-4"
                    >
                        <div class="space-y-2 w-full">
                            <div
                                class="max-w-full bg-white overflow-auto wrap-break-word select-all px-4 py-2 rounded-lg2 border shadow-xs"
                            >
                                {{ shareUrl }}
                            </div>
                        </div>
                        <Button
                            :disabled="cannotCopyToClipboard"
                            @click="copyUrlToClipboard()"
                            color="outline"
                            iconName="copy"
                        >
                            Copy
                        </Button>
                    </div>

                    <div
                        :class="{ invisible: !showCopiedMessage }"
                        class="text-primary text-sm"
                    >
                        Share URL copied!
                    </div>
                    <div
                        :class="{ invisible: !showErrorMessage }"
                        class="text-red-500 text-sm"
                    >
                        Error trying to copy share URL.
                    </div>
                </div>
            </template>

            <template #actions>
                <Button
                    @click="closeModal()"
                    color="light"
                >
                    Close
                </Button>
            </template>
        </Modal>
    </div>
</template>

<script setup lang="ts">
    import { ref, type PropType } from 'vue'
    import ServiceID from '@/App/Service/Entity/ServiceID'
    import Button from '@/Shared/Component/Button.vue'
    import Modal from '@/Shared/Component/Modal.vue'
    import useCopyToClipboard from '@/Shared/Hook/useCopyToClipboard'
    import useRecordAppClipboardAnalyticsEvent from '@/Shared/Hook/useRecordAppClipboardAnalyticsEvent'
    import { AnalyticsEventSurfaceEnum } from '@/Shared/AutoGenerated/Main'

    const props = defineProps({
        shareUrl: {
            type: String,
            required: true,
        },
        buttonSize: {
            type: String as PropType<'normal' | 'small'>,
            required: true,
        },
        analyticsSurface: {
            type: String as PropType<AnalyticsEventSurfaceEnum>,
            required: true,
        },
        analyticsServiceId: {
            type: ServiceID as PropType<ServiceID | undefined>,
            required: false,
            default: undefined,
        },
    })

    // --------
    // Copy to clipboard
    // --------

    const copyToClipboard = useCopyToClipboard()
    const cannotCopyToClipboard = copyToClipboard.isNotAvailable()
    const showCopiedMessage = ref(false)
    const showErrorMessage = ref(false)
    let timeoutHandler: any = undefined

    const copyUrlToClipboard = () => {
        showCopiedMessage.value = false
        showErrorMessage.value = false

        if (timeoutHandler !== undefined) {
            clearTimeout(timeoutHandler)
            timeoutHandler = undefined
        }

        copyToClipboard.copy(
            props.shareUrl,
            () => {
                showCopiedMessage.value = true

                recordAnalyticsEvent()

                timeoutHandler = setTimeout(() => {
                    showCopiedMessage.value = false
                }, 1000)
            },
            () => {
                showErrorMessage.value = true

                timeoutHandler = setTimeout(() => {
                    showErrorMessage.value = false
                }, 1000)
            },
        )
    }

    // --------
    // Modal
    // --------

    const isModalOpen = ref(false)

    const closeModal = () => {
        isModalOpen.value = false
    }

    const openModal = () => {
        isModalOpen.value = true
    }

    // --------
    // Analytics
    // --------

    const recordAppClipboardAnalyticsEvent =
        useRecordAppClipboardAnalyticsEvent()

    const recordAnalyticsEvent = () => {
        void recordAppClipboardAnalyticsEvent.recordBookingLinkCopied(
            props.analyticsSurface,
            props.analyticsServiceId,
        )
    }
</script>
